/**
 * ImageStyleModal.js
 *
 * Drop-in modal for the Bildstil-Panel in Produktion.
 * Handles:
 *   • Single image generation with full style controls
 *   • Carousel generation (one image per slide)
 *   • Inspiration tab  — sends image as style_reference
 *   • Template tab     — canvas mask editor, sends mask to images.edit path
 *
 * Usage in Produktion.js:
 *   import ImageStyleModal from '../../components/ImageStyleModal';
 *   <ImageStyleModal
 *     open={showImageStyleModal}
 *     onOpenChange={setShowImageStyleModal}
 *     contentId={selectedContent?.id}
 *     contentText={selectedContent?.text_content}
 *     platform={selectedContent?.platform}
 *     onImageGenerated={(urls) => { ... }}
 *     getAuthHeader={getAuthHeader}
 *     lang={lang}
 *   />
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Loader2, Wand2, X, Upload, ChevronDown, ChevronUp, Layers } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

// ─── Constants ────────────────────────────────────────────────────────────────

const VISUAL_STYLES = [
  { key: 'echte_menschen', label: 'Echte Menschen', sub: 'Lifestyle & Fotografie' },
  { key: 'illustration',   label: 'Illustration',   sub: 'Zeichnerisch, 2D' },
  { key: '3d_render',      label: '3D / Render',    sub: 'Produktvisualisierung' },
  { key: 'flat_design',    label: 'Flat Design',    sub: 'Ikonen & Vektorgrafik' },
];

const MOODS = [
  { key: 'warm_einladend', label: 'Warm & Einladend' },
  { key: 'clean_minimal',  label: 'Clean & Minimal' },
  { key: 'energetisch',    label: 'Energetisch' },
  { key: 'luxurioeus',     label: 'Luxuriös' },
  { key: 'verspielt',      label: 'Verspielt' },
  { key: 'professionell',  label: 'Professionell' },
  { key: 'natuerlich',     label: 'Natürlich' },
  { key: 'dramatisch',     label: 'Dramatisch' },
];

const COLOR_SCHEMES = [
  { key: 'brand_dna',         label: 'Brand DNA' },
  { key: 'pastell',           label: 'Pastelltöne' },
  { key: 'monochrom',         label: 'Monochrom' },
  { key: 'erdtoene',          label: 'Erdtöne' },
  { key: 'kraeftig_kontrast', label: 'Kräftig & Kontrast' },
  { key: 'schwarz_weiss',     label: 'Schwarz-Weiß' },
];

const ASPECT_RATIOS = [
  { key: '1:1',  label: '1:1',  icon: '▐▌' },
  { key: '4:5',  label: '4:5',  icon: '▐▌▌' },
  { key: '9:16', label: '9:16', icon: '█' },
  { key: '16:9', label: '16:9', icon: '▬' },
];

const LAYOUTS = [
  { key: 'bild_text', label: 'Bild + Text', icon: '▤' },
  { key: 'vollbild',  label: 'Vollbild',    icon: '▣' },
  { key: 'text_only', label: 'Text only',   icon: '▥' },
];

const COVER_TYPES = [
  { key: 'hook_frage',     label: 'Hook-Frage' },
  { key: 'bold_statement', label: 'Bold Statement' },
  { key: 'produktbild',    label: 'Produktbild' },
  { key: 'zahl_fakt',      label: 'Zahl / Fakt' },
];

const STYLE_PRESETS = [
  { id: 'editorial',  name: 'Editorial',  sub: 'Hell & Clean',
    visual_style: 'echte_menschen', mood: ['clean_minimal'],     color_scheme: 'brand_dna' },
  { id: 'bold_brand', name: 'Bold Brand', sub: 'Kräftig & Kontrast',
    visual_style: 'flat_design',    mood: ['energetisch'],        color_scheme: 'kraeftig_kontrast' },
  { id: 'organic',    name: 'Organic',    sub: 'Natürlich & Weich',
    visual_style: 'echte_menschen', mood: ['natuerlich', 'warm_einladend'], color_scheme: 'erdtoene' },
  { id: 'luxe',       name: 'Luxe',       sub: 'Dunkel & Premium',
    visual_style: 'echte_menschen', mood: ['luxurioeus', 'dramatisch'],     color_scheme: 'monochrom' },
  { id: 'playful',    name: 'Playful',    sub: 'Bunt & Verspielt',
    visual_style: 'illustration',   mood: ['verspielt', 'energetisch'],     color_scheme: 'kraeftig_kontrast' },
];

// ─── Tiny shared UI atoms ─────────────────────────────────────────────────────

const SectionLabel = ({ children }) => (
  <div className="text-[10px] font-semibold text-[#5A6C7D] tracking-widest uppercase mb-2">
    {children}
  </div>
);

const Chip = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`px-2.5 py-1 rounded-full text-xs border transition-all ${
      active
        ? 'bg-[#00D9C8]/15 border-[#00D9C8]/60 text-[#00D9C8]'
        : 'bg-[#0A1628] border-[#1A2B42] text-[#5A6C7D] hover:border-[#2A3B52] hover:text-[#A0AEC0]'
    }`}
  >
    {children}
  </button>
);

const StyleOpt = ({ selected, onClick, title, sub }) => (
  <button
    onClick={onClick}
    className={`text-left p-2.5 rounded border transition-all ${
      selected
        ? 'border-[#00D9C8]/60 bg-[#00D9C8]/10 text-[#00D9C8]'
        : 'border-[#1A2B42] bg-[#0A1628] text-[#A0AEC0] hover:border-[#2A3B52]'
    }`}
  >
    <div className="text-xs font-medium">{title}</div>
    <div className={`text-[10px] mt-0.5 ${selected ? 'text-[#00D9C8]/70' : 'text-[#5A6C7D]'}`}>{sub}</div>
  </button>
);

const Divider = () => <div className="h-px bg-[#1A2B42] my-3" />;

// ─── Mask Editor ──────────────────────────────────────────────────────────────

const MaskEditor = ({ templateBase64, onMaskReady }) => {
  const canvasRef = useRef(null);
  const isDrawing = useRef(false);
  const [brushSize, setBrushSize] = useState(40);
  const [maskDone, setMaskDone] = useState(false);

  useEffect(() => {
    if (!templateBase64 || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      // Fit to canvas width 480px
      const scale = 480 / img.width;
      canvas.width = 480;
      canvas.height = img.height * scale;
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      // semi-transparent overlay to indicate "unpainted" areas
      ctx.fillStyle = 'rgba(0,217,200,0.08)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    };
    img.src = `data:image/png;base64,${templateBase64}`;
  }, [templateBase64]);

  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const scaleX = canvasRef.current.width / rect.width;
    const scaleY = canvasRef.current.height / rect.height;
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  };

  const paint = useCallback((e) => {
    if (!isDrawing.current || !canvasRef.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getPos(e);
    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.arc(x, y, brushSize / 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalCompositeOperation = 'source-over';
  }, [brushSize]);

  const buildMask = () => {
    if (!canvasRef.current) return;
    // Create a B&W mask: painted areas (transparent) = white (fill zone), rest = black
    const src = canvasRef.current;
    const mask = document.createElement('canvas');
    mask.width = src.width;
    mask.height = src.height;
    const mCtx = mask.getContext('2d');
    mCtx.fillStyle = 'black';
    mCtx.fillRect(0, 0, mask.width, mask.height);
    const imgData = src.getContext('2d').getImageData(0, 0, src.width, src.height);
    const mData = mCtx.getImageData(0, 0, mask.width, mask.height);
    for (let i = 3; i < imgData.data.length; i += 4) {
      if (imgData.data[i] < 128) {
        // Transparent pixel on canvas = fill zone → white in mask
        const px = (i - 3) / 4;
        mData.data[px * 4] = 255;
        mData.data[px * 4 + 1] = 255;
        mData.data[px * 4 + 2] = 255;
        mData.data[px * 4 + 3] = 255;
      }
    }
    mCtx.putImageData(mData, 0, 0);
    const maskB64 = mask.toDataURL('image/png').split(',')[1];
    onMaskReady(maskB64);
    setMaskDone(true);
  };

  const resetMask = () => {
    setMaskDone(false);
    onMaskReady(null);
    // Redraw image
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      ctx.fillStyle = 'rgba(0,217,200,0.08)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    };
    img.src = `data:image/png;base64,${templateBase64}`;
  };

  return (
    <div className="space-y-3">
      <div className="text-xs text-[#A0AEC0]">
        Male den Bereich den die KI <span className="text-[#00D9C8]">befüllen</span> soll — der Rest bleibt exakt erhalten.
      </div>
      <div className="flex items-center gap-3 text-xs text-[#5A6C7D]">
        <span>Pinselgröße</span>
        <input
          type="range" min={10} max={80} value={brushSize}
          onChange={e => setBrushSize(Number(e.target.value))}
          className="flex-1 accent-[#00D9C8]"
        />
        <span className="w-8 text-right text-[#A0AEC0]">{brushSize}px</span>
      </div>
      <div
        className="relative rounded overflow-hidden border border-[#1A2B42] cursor-crosshair"
        style={{ background: 'repeating-conic-gradient(#1A2B42 0% 25%, #0A1628 0% 50%) 0 0 / 16px 16px' }}
      >
        <canvas
          ref={canvasRef}
          style={{ width: '100%', display: 'block', touchAction: 'none' }}
          onMouseDown={(e) => { isDrawing.current = true; paint(e); }}
          onMouseMove={paint}
          onMouseUp={() => { isDrawing.current = false; }}
          onMouseLeave={() => { isDrawing.current = false; }}
          onTouchStart={(e) => { isDrawing.current = true; paint(e); }}
          onTouchMove={paint}
          onTouchEnd={() => { isDrawing.current = false; }}
        />
      </div>
      <div className="flex gap-2">
        <Button
          onClick={buildMask}
          size="sm"
          className="flex-1 bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628] text-xs"
        >
          {maskDone ? '✓ Maske gespeichert' : 'Maske bestätigen'}
        </Button>
        {maskDone && (
          <Button onClick={resetMask} size="sm" variant="outline"
            className="border-[#1A2B42] text-[#5A6C7D] text-xs">
            Zurücksetzen
          </Button>
        )}
      </div>
    </div>
  );
};

// ─── Thumbnail strip ──────────────────────────────────────────────────────────

const ThumbStrip = ({ items, selectedId, onSelect }) => (
  <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-thin scrollbar-thumb-[#1A2B42]">
    {items.map(item => (
      <button
        key={item.id}
        onClick={() => onSelect(item)}
        className={`relative flex-shrink-0 w-[72px] group ${selectedId === item.id ? 'ring-2 ring-[#00D9C8] rounded' : ''}`}
      >
        <img
          src={item.image_url || item.preview}
          alt={item.label}
          className="w-[72px] h-[72px] object-cover rounded border border-[#1A2B42] group-hover:border-[#2A3B52]"
        />
        {selectedId === item.id && (
          <div className="absolute top-1 right-1 w-4 h-4 rounded-full bg-[#00D9C8] flex items-center justify-center">
            <span className="text-[#0A1628] text-[8px] font-bold">✓</span>
          </div>
        )}
        <div className="text-[10px] text-[#5A6C7D] text-center mt-1 truncate">{item.label}</div>
      </button>
    ))}
  </div>
);

// ─── Main Modal ───────────────────────────────────────────────────────────────

const ImageStyleModal = ({
  open,
  onOpenChange,
  contentId,
  contentText = '',
  platform = 'instagram',
  onImageGenerated,
  getAuthHeader,
  lang = 'de',
}) => {
  // ── Mode ──────────────────────────────────────────────────────────────────
  const [mode, setMode] = useState('single');

  // ── Single controls ───────────────────────────────────────────────────────
  const [freeText, setFreeText] = useState('');
  const [visualStyle, setVisualStyle] = useState('');
  const [moods, setMoods] = useState([]);
  const [colorScheme, setColorScheme] = useState('brand_dna');
  const [aspectRatio, setAspectRatio] = useState('1:1');
  const [selectedProducts, setSelectedProducts] = useState([]);

  // ── Carousel controls ─────────────────────────────────────────────────────
  const [slideContents, setSlideContents] = useState(['', '', '', '', '']);
  const [layout, setLayout] = useState('bild_text');
  const [coverType, setCoverType] = useState('hook_frage');

  // ── Style reference ───────────────────────────────────────────────────────
  const [refTab, setRefTab] = useState('meine');   // meine | presets | hochladen | templates
  const [selectedRef, setSelectedRef] = useState(null);
  const [refStrength, setRefStrength] = useState(70);
  const [refType, setRefType] = useState('inspiration'); // inspiration | template
  const [templateBase64, setTemplateBase64] = useState(null);
  const [maskBase64, setMaskBase64] = useState(null);

  // ── Brand products from DB ────────────────────────────────────────────────
  const [brandProducts, setBrandProducts] = useState([]);

  // ── Recent posts for "Meine Posts" tab ───────────────────────────────────
  const [recentPosts, setRecentPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(false);

  // ── Generation state ──────────────────────────────────────────────────────
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState('');

  const fileInputRef = useRef(null);

  // ── Fetch data on open ───────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;

    // Fetch brand hauptprodukt
    axios.get(`${API}/brand/hauptprodukt`, { headers: getAuthHeader() })
      .then(r => {
        const hp = r.data?.hauptprodukt;
        if (hp?.name) setBrandProducts([{ key: hp.name, label: hp.name, emoji: hp.hp_type === 'person' ? '👤' : '📦' }]);
      })
      .catch(() => {});

    // Fetch recent posts with images for style reference
    setLoadingPosts(true);
    axios.get(`${API}/content?limit=15`, { headers: getAuthHeader() })
      .then(r => {
        const posts = (r.data?.content_pieces || [])
          .filter(p => p.image_urls?.length > 0)
          .slice(0, 10)
          .map(p => ({
            id: p.id,
            label: p.title?.slice(0, 20) || `#${p.content_number}`,
            image_url: p.image_urls[0],
          }));
        setRecentPosts(posts);
      })
      .catch(() => {})
      .finally(() => setLoadingPosts(false));
  }, [open]);

  // ── Handlers ─────────────────────────────────────────────────────────────
  const toggleMood = (key) =>
    setMoods(prev => prev.includes(key) ? prev.filter(m => m !== key) : [...prev, key]);

  const toggleProduct = (key) =>
    setSelectedProducts(prev => prev.includes(key) ? prev.filter(p => p !== key) : [...prev, key]);

  const applyPreset = (preset) => {
    setVisualStyle(preset.visual_style);
    setMoods(preset.mood);
    setColorScheme(preset.color_scheme);
    setSelectedRef({ id: preset.id, label: preset.name, sub: preset.sub, isPreset: true });
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const b64 = ev.target.result.split(',')[1];
      if (refType === 'template') {
        setTemplateBase64(b64);
        setMaskBase64(null);
      } else {
        setSelectedRef({ id: 'upload', label: file.name, image_url: ev.target.result, b64 });
      }
    };
    reader.readAsDataURL(file);
  };

  // ── Build payload ─────────────────────────────────────────────────────────
  const buildPayload = (slideIndex = null) => {
    const ref = selectedRef
      ? {
          source: refTab === 'meine' ? 'meine_posts' : refTab === 'presets' ? 'presets' : 'hochladen',
          name: selectedRef.label,
          sub: selectedRef.sub || '',
          strength: refStrength,
          // Pass b64 for uploaded references so backend can use them
          image_b64: selectedRef.b64 || null,
          content_id_ref: selectedRef.id && selectedRef.id !== 'upload' && !selectedRef.isPreset
            ? selectedRef.id : null,
        }
      : null;

    const base = {
      mode,
      free_text: freeText || null,
      visual_style: visualStyle || null,
      mood: moods.length ? moods : null,
      color_scheme: colorScheme,
      aspect_ratio: aspectRatio,
      selected_products: selectedProducts.length ? selectedProducts : null,
      style_reference: ref,
      style_reference_type: refType,
      template_mask_base64: refType === 'template' ? maskBase64 : null,
      template_image_base64: refType === 'template' ? templateBase64 : null,
    };

    if (mode === 'carousel') {
      return {
        ...base,
        slide_contents: slideContents.filter(s => s.trim()),
        layout,
        cover_type: coverType,
      };
    }
    return base;
  };

  // ── Generate ──────────────────────────────────────────────────────────────
  const handleGenerate = async () => {
    if (!contentId) return;

    // Validate template mode
    if (refType === 'template' && templateBase64 && !maskBase64) {
      toast.error('Bitte bestätige zuerst die Maske im Template-Editor.');
      return;
    }

    setIsGenerating(true);
    const nSlides = mode === 'carousel' ? slideContents.filter(s => s.trim()).length : 1;
    setGenerationProgress(
      mode === 'carousel'
        ? `Generiere ${nSlides} Slides...`
        : 'Erstelle optimalen Bildstil...'
    );

    try {
      const response = await axios.post(
        `${API}/content/${contentId}/generate-image-styled`,
        { image_style_controls: buildPayload() },
        { headers: getAuthHeader(), timeout: 120000 }
      );

      if (mode === 'single') {
        const url = response.data.image_url;
        if (url) {
          onImageGenerated?.([url]);
          toast.success('Bild generiert');
        }
      } else {
        const urls = (response.data.slides || [])
          .filter(s => s.image_url)
          .map(s => s.image_url);
        if (urls.length) {
          onImageGenerated?.(urls);
          toast.success(`${urls.length} Carousel-Bilder generiert`);
        }
        const failed = (response.data.slides || []).filter(s => s.error).length;
        if (failed) toast.warning(`${failed} Slide(s) fehlgeschlagen`);
      }

      onOpenChange(false);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Bildgenerierung fehlgeschlagen';
      toast.error(msg);
    } finally {
      setIsGenerating(false);
      setGenerationProgress('');
    }
  };

  // ── Strength hint ─────────────────────────────────────────────────────────
  const strengthHint =
    refStrength <= 30 ? 'Sehr schwache Stilanleihen' :
    refStrength <= 50 ? 'Moderat — Stimmung übernommen' :
    refStrength <= 75 ? 'Ausgewogen — Stil erkennbar' :
    'Stark — nah am Referenzbild';

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0D1F35] border-[#1A2B42] text-white max-w-lg w-full max-h-[90vh] overflow-y-auto p-0">

        {/* Header */}
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-[#1A2B42]">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-sm font-semibold text-white flex items-center gap-2">
              <Wand2 className="w-4 h-4 text-[#00D9C8]" />
              Bildstil anpassen
            </DialogTitle>
            <button onClick={() => onOpenChange(false)}
              className="text-[#5A6C7D] hover:text-white transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>
        </DialogHeader>

        <div className="px-5 py-4 space-y-4">

          {/* ── Mode switch ─────────────────────────────────────────────── */}
          <div className="flex rounded border border-[#1A2B42] overflow-hidden">
            {['single', 'carousel'].map(m => (
              <button
                key={m}
                onClick={() => setMode(m)}
                className={`flex-1 py-2 text-xs font-medium transition-all ${
                  mode === m
                    ? 'bg-[#0A1628] text-white shadow-inner'
                    : 'bg-[#1A2B42]/30 text-[#5A6C7D] hover:text-[#A0AEC0]'
                }`}
              >
                {m === 'single' ? '▤ Einzelbild' : '⊞ Carousel'}
              </button>
            ))}
          </div>

          {/* ══════════════ SINGLE MODE ══════════════ */}
          {mode === 'single' && (
            <>
              {/* Free text */}
              <div>
                <SectionLabel>Freie Beschreibung</SectionLabel>
                <textarea
                  value={freeText}
                  onChange={e => setFreeText(e.target.value)}
                  placeholder="z.B. warme Sommerstimmung, Outdoor, helle Farben..."
                  rows={2}
                  className="w-full bg-[#0A1628] border border-[#1A2B42] rounded px-3 py-2 text-sm text-white placeholder-[#3A4B5C] focus:border-[#00D9C8]/50 focus:outline-none resize-none"
                />
              </div>

              <Divider />

              {/* Visual style */}
              <div>
                <SectionLabel>Visueller Stil</SectionLabel>
                <div className="grid grid-cols-2 gap-2">
                  {VISUAL_STYLES.map(vs => (
                    <StyleOpt
                      key={vs.key}
                      selected={visualStyle === vs.key}
                      onClick={() => setVisualStyle(vs.key === visualStyle ? '' : vs.key)}
                      title={vs.label}
                      sub={vs.sub}
                    />
                  ))}
                </div>
              </div>

              <Divider />

              {/* Products */}
              {brandProducts.length > 0 && (
                <>
                  <div>
                    <SectionLabel>Produkt / Asset zeigen</SectionLabel>
                    <div className="flex flex-wrap gap-2">
                      {brandProducts.map(p => (
                        <Chip
                          key={p.key}
                          active={selectedProducts.includes(p.key)}
                          onClick={() => toggleProduct(p.key)}
                        >
                          {p.emoji} {p.label}
                        </Chip>
                      ))}
                    </div>
                  </div>
                  <Divider />
                </>
              )}

              {/* Mood */}
              <div>
                <SectionLabel>Stimmung</SectionLabel>
                <div className="flex flex-wrap gap-1.5">
                  {MOODS.map(m => (
                    <Chip key={m.key} active={moods.includes(m.key)} onClick={() => toggleMood(m.key)}>
                      {m.label}
                    </Chip>
                  ))}
                </div>
              </div>

              <Divider />

              {/* Colour */}
              <div>
                <SectionLabel>Farbgebung</SectionLabel>
                <div className="flex flex-wrap gap-1.5">
                  {COLOR_SCHEMES.map(c => (
                    <Chip key={c.key} active={colorScheme === c.key} onClick={() => setColorScheme(c.key)}>
                      {c.label}
                    </Chip>
                  ))}
                </div>
              </div>

              <Divider />

              {/* Format */}
              <div>
                <SectionLabel>Format</SectionLabel>
                <div className="flex gap-2">
                  {ASPECT_RATIOS.map(a => (
                    <button
                      key={a.key}
                      onClick={() => setAspectRatio(a.key)}
                      className={`flex-1 py-2 rounded border text-xs transition-all ${
                        aspectRatio === a.key
                          ? 'border-[#00D9C8]/60 bg-[#00D9C8]/10 text-[#00D9C8]'
                          : 'border-[#1A2B42] text-[#5A6C7D] hover:border-[#2A3B52]'
                      }`}
                    >
                      <div className="text-base leading-none mb-1">{a.icon}</div>
                      {a.label}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* ══════════════ CAROUSEL MODE ══════════════ */}
          {mode === 'carousel' && (
            <>
              {/* Slide count + content */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <SectionLabel>Inhalt pro Slide</SectionLabel>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setSlideContents(prev => prev.length > 2 ? prev.slice(0, -1) : prev)}
                      className="w-6 h-6 rounded border border-[#1A2B42] text-[#A0AEC0] text-sm hover:border-[#00D9C8] flex items-center justify-center"
                    >−</button>
                    <span className="text-xs text-white w-4 text-center">{slideContents.length}</span>
                    <button
                      onClick={() => setSlideContents(prev => prev.length < 10 ? [...prev, ''] : prev)}
                      className="w-6 h-6 rounded border border-[#1A2B42] text-[#A0AEC0] text-sm hover:border-[#00D9C8] flex items-center justify-center"
                    >＋</button>
                  </div>
                </div>
                <div className="space-y-1.5">
                  {slideContents.map((sc, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <span className="text-[10px] text-[#5A6C7D] pt-2 w-5 text-right flex-shrink-0">
                        {idx + 1}
                        {idx === 0 && (
                          <span className="ml-1 text-[9px] px-1 py-0.5 rounded bg-[#F59E0B]/15 text-[#F59E0B]">Cover</span>
                        )}
                      </span>
                      <input
                        value={sc}
                        onChange={e => {
                          const next = [...slideContents];
                          next[idx] = e.target.value;
                          setSlideContents(next);
                        }}
                        placeholder={idx === 0 ? 'Cover: Hook oder Frage' : `Slide ${idx + 1} Inhalt`}
                        className="flex-1 bg-[#0A1628] border border-[#1A2B42] rounded px-2.5 py-1.5 text-xs text-white placeholder-[#3A4B5C] focus:border-[#00D9C8]/50 focus:outline-none"
                      />
                    </div>
                  ))}
                </div>
                <p className="text-[10px] text-[#5A6C7D] mt-1.5">
                  Jede Slide bekommt ein eigenes Bild im gleichen Stil.
                </p>
              </div>

              <Divider />

              {/* Layout */}
              <div>
                <SectionLabel>Layout</SectionLabel>
                <div className="grid grid-cols-3 gap-2">
                  {LAYOUTS.map(l => (
                    <button
                      key={l.key}
                      onClick={() => setLayout(l.key)}
                      className={`py-2 rounded border text-xs transition-all ${
                        layout === l.key
                          ? 'border-[#00D9C8]/60 bg-[#00D9C8]/10 text-[#00D9C8]'
                          : 'border-[#1A2B42] text-[#5A6C7D] hover:border-[#2A3B52]'
                      }`}
                    >
                      <div className="text-base mb-1">{l.icon}</div>
                      {l.label}
                    </button>
                  ))}
                </div>
              </div>

              <Divider />

              {/* Cover type */}
              <div>
                <SectionLabel>Cover-Slide Typ</SectionLabel>
                <div className="flex flex-wrap gap-1.5">
                  {COVER_TYPES.map(ct => (
                    <Chip key={ct.key} active={coverType === ct.key} onClick={() => setCoverType(ct.key)}>
                      {ct.label}
                    </Chip>
                  ))}
                </div>
                <p className="text-[10px] text-[#5A6C7D] mt-1.5">
                  Die erste Slide entscheidet ob jemand weiterwischt.
                </p>
              </div>

              <Divider />

              {/* Visual style for carousel */}
              <div>
                <SectionLabel>Stil (gilt für alle Slides)</SectionLabel>
                <div className="grid grid-cols-2 gap-2">
                  {VISUAL_STYLES.map(vs => (
                    <StyleOpt
                      key={vs.key}
                      selected={visualStyle === vs.key}
                      onClick={() => setVisualStyle(vs.key === visualStyle ? '' : vs.key)}
                      title={vs.label}
                      sub={vs.sub}
                    />
                  ))}
                </div>
              </div>
            </>
          )}

          {/* ══════════════ STIL-REFERENZ (both modes) ══════════════ */}
          <Divider />
          <div>
            <SectionLabel>Stil-Referenz</SectionLabel>

            {/* Tab row */}
            <div className="flex gap-1.5 mb-3 flex-wrap">
              {['meine', 'presets', 'hochladen', 'templates'].map(tab => (
                <button
                  key={tab}
                  onClick={() => {
                    setRefTab(tab);
                    if (tab === 'templates') setRefType('template');
                    else setRefType('inspiration');
                  }}
                  className={`px-2.5 py-1 rounded-full text-[11px] border transition-all ${
                    refTab === tab
                      ? 'bg-[#00D9C8]/15 border-[#00D9C8]/60 text-[#00D9C8]'
                      : 'border-[#1A2B42] text-[#5A6C7D] hover:border-[#2A3B52]'
                  }`}
                >
                  {tab === 'meine' ? 'Meine Posts' :
                   tab === 'presets' ? 'Presets' :
                   tab === 'hochladen' ? 'Hochladen' : 'Templates'}
                </button>
              ))}
            </div>

            {/* Meine Posts */}
            {refTab === 'meine' && (
              loadingPosts ? (
                <div className="flex items-center gap-2 text-xs text-[#5A6C7D] py-3">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Lade Posts...
                </div>
              ) : recentPosts.length > 0 ? (
                <ThumbStrip
                  items={recentPosts}
                  selectedId={selectedRef?.id}
                  onSelect={(item) => setSelectedRef(
                    selectedRef?.id === item.id ? null : { ...item, label: item.label }
                  )}
                />
              ) : (
                <p className="text-xs text-[#5A6C7D] py-3">Noch keine Posts mit Bildern vorhanden.</p>
              )
            )}

            {/* Presets */}
            {refTab === 'presets' && (
              <div className="space-y-1.5">
                {STYLE_PRESETS.map(p => (
                  <button
                    key={p.id}
                    onClick={() => {
                      applyPreset(p);
                      setRefTab('presets');
                    }}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded border text-xs transition-all ${
                      selectedRef?.id === p.id
                        ? 'border-[#00D9C8]/60 bg-[#00D9C8]/10 text-[#00D9C8]'
                        : 'border-[#1A2B42] bg-[#0A1628] text-[#A0AEC0] hover:border-[#2A3B52]'
                    }`}
                  >
                    <span className="font-medium">{p.name}</span>
                    <span className={`text-[10px] ${selectedRef?.id === p.id ? 'text-[#00D9C8]/70' : 'text-[#5A6C7D]'}`}>
                      {p.sub}
                    </span>
                  </button>
                ))}
              </div>
            )}

            {/* Hochladen */}
            {refTab === 'hochladen' && (
              <div>
                <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileUpload} />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full border border-dashed border-[#1A2B42] rounded p-4 text-center hover:border-[#2A3B52] transition-colors"
                >
                  <Upload className="w-5 h-5 mx-auto mb-1 text-[#5A6C7D]" />
                  <p className="text-xs text-[#5A6C7D]">Bild hochladen oder per URL einfügen</p>
                  <p className="text-[10px] text-[#3A4B5C] mt-1">Wird als Stil-Inspiration verwendet, nicht 1:1 kopiert</p>
                </button>
                {selectedRef?.id === 'upload' && (
                  <div className="flex items-center gap-2 mt-2 text-xs text-[#00D9C8]">
                    <img src={selectedRef.image_url} alt="" className="w-8 h-8 rounded object-cover" />
                    {selectedRef.label}
                    <button onClick={() => setSelectedRef(null)} className="ml-auto text-[#5A6C7D] hover:text-white">✕</button>
                  </div>
                )}
              </div>
            )}

            {/* Templates */}
            {refTab === 'templates' && (
              <div className="space-y-3">
                <div className="text-xs text-[#A0AEC0]">
                  Lade ein Template-Bild hoch. Die KI befüllt den von dir markierten Bereich — der Rest bleibt exakt erhalten.
                </div>
                {!templateBase64 ? (
                  <>
                    <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileUpload} />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full border border-dashed border-[#1A2B42] rounded p-4 text-center hover:border-[#2A3B52] transition-colors"
                    >
                      <Upload className="w-5 h-5 mx-auto mb-1 text-[#5A6C7D]" />
                      <p className="text-xs text-[#5A6C7D]">Template hochladen (JPG / PNG)</p>
                    </button>
                  </>
                ) : (
                  <>
                    <MaskEditor
                      templateBase64={templateBase64}
                      onMaskReady={setMaskBase64}
                    />
                    <button
                      onClick={() => { setTemplateBase64(null); setMaskBase64(null); }}
                      className="text-xs text-[#5A6C7D] hover:text-white"
                    >
                      ← Anderes Template wählen
                    </button>
                  </>
                )}
              </div>
            )}

            {/* Strength slider — shown when a non-template reference is selected */}
            {selectedRef && refType === 'inspiration' && (
              <div className="mt-3 p-3 rounded border border-[#00D9C8]/20 bg-[#00D9C8]/5 space-y-2">
                <div className="flex items-center gap-2 text-xs text-[#A0AEC0]">
                  <img
                    src={selectedRef.image_url || selectedRef.preview}
                    alt=""
                    className="w-8 h-8 rounded object-cover flex-shrink-0"
                    onError={e => { e.target.style.display = 'none'; }}
                  />
                  <span className="font-medium text-[#00D9C8]">{selectedRef.label}</span>
                  <button onClick={() => setSelectedRef(null)} className="ml-auto text-[#5A6C7D] hover:text-white">✕</button>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-[#5A6C7D] w-14">Stärke</span>
                  <input
                    type="range" min={10} max={100} step={10} value={refStrength}
                    onChange={e => setRefStrength(Number(e.target.value))}
                    className="flex-1 accent-[#00D9C8]"
                  />
                  <span className="text-[10px] text-[#A0AEC0] w-7 text-right">{refStrength}%</span>
                </div>
                <p className="text-[10px] text-[#5A6C7D]">{strengthHint}</p>
              </div>
            )}
          </div>

        </div>

        {/* Footer */}
        <div className="px-5 pb-5 pt-2 border-t border-[#1A2B42]">
          <Button
            onClick={handleGenerate}
            disabled={isGenerating}
            className="w-full bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628] font-semibold"
          >
            {isGenerating ? (
              <span className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                {generationProgress || 'Generiere...'}
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Wand2 className="w-4 h-4" />
                {mode === 'carousel' ? `${slideContents.filter(s=>s.trim()).length} Bilder generieren` : 'Bild generieren'}
              </span>
            )}
          </Button>
        </div>

      </DialogContent>
    </Dialog>
  );
};

export default ImageStyleModal;